import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { MapContainer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import { api } from '../../services/api';
import { FrontDoorTileLayer } from '../Map/FrontDoorTileLayer';

// Fix default marker icon (Leaflet webpack/bundler issue)
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

interface DeviceLocationTabProps {
  device: any;
  deviceId: string;
}

const TIME_RANGES = [
  { label: '24h', days: 1 },
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
] as const;

function DeviceLocationTab({ device, deviceId }: DeviceLocationTabProps) {
  const [daysRange, setDaysRange] = useState(7);

  const { data: locationData } = useQuery({
    queryKey: ['device-location-history', deviceId, daysRange],
    queryFn: () => api.adminGetDeviceLocationHistory(deviceId, daysRange),
  });

  // Extract polyline coordinates from location history (handles both formats)
  const trackCoordinates = useMemo<[number, number][]>(() => {
    if (!locationData) return [];

    // Format 1: GeoJSON LineString
    if (locationData.geojson?.geometry?.coordinates) {
      const coords = locationData.geojson.geometry.coordinates;
      // GeoJSON is [lon, lat], Leaflet expects [lat, lon]
      return coords.map((c: [number, number]) => [c[1], c[0]] as [number, number]);
    }

    // Format 2: locations array with { lat, lon, recorded_at }
    if (locationData.locations && Array.isArray(locationData.locations)) {
      return locationData.locations.map((loc: { lat: number; lon: number }) => [loc.lat, loc.lon] as [number, number]);
    }

    return [];
  }, [locationData]);

  // Compute track stats
  const trackStats = useMemo(() => {
    const totalPoints = trackCoordinates.length;

    let dateRange = '';
    if (locationData?.locations && locationData.locations.length > 0) {
      const sorted = [...locationData.locations].sort(
        (a: { recorded_at: string }, b: { recorded_at: string }) =>
          new Date(a.recorded_at).getTime() - new Date(b.recorded_at).getTime()
      );
      const first = format(new Date(sorted[0].recorded_at), 'd MMM yyyy HH:mm');
      const last = format(new Date(sorted[sorted.length - 1].recorded_at), 'd MMM yyyy HH:mm');
      dateRange = `${first} - ${last}`;
    } else if (trackCoordinates.length > 0) {
      dateRange = `Last ${daysRange} day${daysRange > 1 ? 's' : ''}`;
    }

    return { totalPoints, dateRange };
  }, [trackCoordinates, locationData, daysRange]);

  const lastPosition = device?.last_known_position;
  const hasPosition = lastPosition && typeof lastPosition.lat === 'number' && typeof lastPosition.lon === 'number';
  const center: [number, number] = hasPosition ? [lastPosition.lat, lastPosition.lon] : [0, 0];

  if (!hasPosition && trackCoordinates.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 rounded-lg border border-slate-700 bg-slate-800/50">
        <p className="text-sm text-slate-500">No location data available for this device</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Time Range Selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-slate-400">Time range:</span>
        {TIME_RANGES.map((range) => (
          <button
            key={range.days}
            onClick={() => setDaysRange(range.days)}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              daysRange === range.days
                ? 'bg-cyan-600 text-white'
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            {range.label}
          </button>
        ))}
      </div>

      {/* Map */}
      <div className="rounded-lg border border-slate-700 overflow-hidden">
        <MapContainer
          center={center}
          zoom={12}
          className="h-[500px] w-full"
          scrollWheelZoom={true}
        >
          <FrontDoorTileLayer
            source="osm"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            fallbackUrl="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          {/* Current position marker */}
          {hasPosition && (
            <Marker position={[lastPosition.lat, lastPosition.lon]}>
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold">Current Position</p>
                  <p>Lat: {lastPosition.lat.toFixed(6)}</p>
                  <p>Lon: {lastPosition.lon.toFixed(6)}</p>
                  {device.last_seen_at && (
                    <p className="text-gray-500 mt-1">
                      Last seen: {format(new Date(device.last_seen_at), 'd MMM yyyy HH:mm')}
                    </p>
                  )}
                </div>
              </Popup>
            </Marker>
          )}

          {/* Historical track */}
          {trackCoordinates.length > 1 && (
            <Polyline
              positions={trackCoordinates}
              pathOptions={{ color: '#06b6d4', weight: 2, opacity: 0.7 }}
            />
          )}
        </MapContainer>
      </div>

      {/* Track Stats Panel */}
      {trackCoordinates.length > 0 && (
        <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-slate-400">Total Points</p>
              <p className="text-sm text-white font-medium">{trackStats.totalPoints}</p>
            </div>
            <div>
              <p className="text-xs text-slate-400">Date Range</p>
              <p className="text-sm text-white font-medium">{trackStats.dateRange || '—'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeviceLocationTab;
